import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { longDate } from './backfillWindow'
import type { Offer } from './useSaveRule'
import './rules.css'

type Phase = { k: 'asking' } | { k: 'applying' } | { k: 'failed'; message: string }

/**
 * "This will categorise N past transactions" — the stated count a backfill
 * waits on (backend #439). An `alertdialog` stacked over the builder when it
 * comes from Save: `Overlay` traps focus in this, the top panel, and its
 * Escape stops at this panel, so the builder beneath keeps its draft.
 *
 * `--accent` fills the apply button only — the one primary action here.
 */
export function BackfillConfirm({
  offer,
  phase,
  categoryName,
  onApply,
  onSkip,
  onBack,
  hideBack = false,
}: {
  offer: Offer
  phase: Phase
  /** The `set_category` target's name, or null when the rule sets none. */
  categoryName: string | null
  onApply: () => void
  onSkip: () => void
  onBack: () => void
  /**
   * A direct save has no builder to go back to, so a Back button would only duplicate "Skip
   * past". It is left out; Escape and the scrim still reach `onBack`.
   */
  hideBack?: boolean
}) {
  const categorises = offer.categoryId !== null
  const n = `${offer.atLeast ? 'at least ' : ''}${offer.count}`
  const title = `This will ${categorises ? 'categorise' : 'update'} ${n} past transaction${offer.count === 1 ? '' : 's'}`
  const applying = phase.k === 'applying'
  const turnOn = offer.via === 'enable'
  const apply = turnOn ? `Turn on and apply to ${offer.count}` : `Save and apply to ${offer.count}`
  const skip = turnOn ? 'Turn on, skip past' : 'Save, skip past'
  const back = turnOn ? 'Cancel' : 'Back to rule'
  // Every control refuses while the batch is in flight: it has been sent, so
  // backing out now would hide the outcome the user is waiting on.
  const guard = (f: () => void) => () => {
    if (!applying) f()
  }

  return (
    <Overlay open onClose={guard(onBack)} role="alertdialog" label={title}>
      <div className="rule-confirm">
        <h2 className="rule-confirm__title">{title}</h2>
        <p className="rule-confirm__body">
          {categoryName !== null
            ? `Each one gets “${categoryName}”, plus the rule’s other changes.`
            : 'Each one gets the rule’s changes.'}
        </p>
        <ul className="rule-confirm__facts">
          <li>
            From {longDate(offer.from)} to today{offer.defaulted ? ' (last 90 days)' : ''}
          </li>
          <li>Only transactions with no category — {offer.alreadySet} already categorised are left alone</li>
          {offer.skippedLocked > 0 && <li>{offer.skippedLocked} you edited by hand are left alone</li>}
          <li>You can undo the whole batch right after.</li>
        </ul>
        {/* Present from the start so the change is announced: a live region
            added with its text is read by nothing. */}
        <p className="rule-confirm__status" role="status">
          {applying ? `Applying to ${offer.count} past transaction${offer.count === 1 ? '' : 's'}…` : ''}
        </p>
        {phase.k === 'failed' && (
          <p className="rule-confirm__error" role="alert">
            {phase.message}
          </p>
        )}
        <div className="rule-confirm__actions">
          {phase.k === 'failed' ? (
            <>
              <Button variant="outline" size="page" onClick={onBack}>
                Close
              </Button>
              <Button variant="primary" size="page" onClick={onApply}>
                Try again
              </Button>
            </>
          ) : (
            <>
              {!hideBack && (
                <Button variant="outline" size="page" aria-disabled={applying} onClick={guard(onBack)}>
                  {back}
                </Button>
              )}
              <Button variant="outline" size="page" aria-disabled={applying} onClick={guard(onSkip)}>
                {skip}
              </Button>
              <Button
                variant="primary"
                size="page"
                aria-disabled={applying}
                aria-busy={applying}
                onClick={guard(onApply)}
              >
                {applying ? 'Applying…' : apply}
              </Button>
            </>
          )}
        </div>
      </div>
    </Overlay>
  )
}
